import { Grid, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useRef, useState } from "react";
import LaddaButton from "../../../components/buttons/laddaButton";
import ImageUploadButton from "../../../components/ImageUploadBtn";
import "./styles.css";

const MAX_TOTAL_IMAGES = 20;
const MAIN_IMAGES_COUNT = 4;

const createEmptyAdditionalImage = () => ({
  id: 0,
  file: null,
  path: null,
  description: "",
  isNew: true
});

const AnnexesPhotographicEvidence = ({
  images,
  setImages,
  additionalImages,
  setAdditionalImages,
  isApprover,
  isViewer,
  isLocked,
  startProtectedAction,
  endProtectedAction,
  isBusy = false,
  imagesLoading = false,
  onProcessingChange
}) => {
  /**
   * Fallback temporal:
   * Si todavía no conectas additionalImages desde el padre,
   * el componente funciona visualmente con estado local.
   */
  const [localAdditionalImages, setLocalAdditionalImages] = useState([]);
  const addLockRef = useRef(false);
  const [isAdding, setIsAdding] = useState(false);

  const evidences = additionalImages ?? localAdditionalImages;
  const setEvidences = setAdditionalImages ?? setLocalAdditionalImages;

  const isDisabled = isApprover || isViewer || isLocked || isBusy || imagesLoading;
  const totalImages = MAIN_IMAGES_COUNT + evidences.length;
  const canAddMoreImages = totalImages < MAX_TOTAL_IMAGES && !isDisabled;

  const handleImageChange = async (file, imageKey) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);

      const updatedImages = {
        ...images,
        [imageKey]: {
          ...images[imageKey],
          path: previewUrl,
          file
        }
      };

      setImages(updatedImages);
    } else {
      const updatedImages = {
        ...images,
        [imageKey]: {
          ...images[imageKey],
          path: null,
          file: null
        }
      };

      setImages(updatedImages);
    }
  };

  const handleDescriptionChange = (description, imageKey) => {
    const updatedImages = {
      ...images,
      [imageKey]: {
        ...images[imageKey],
        description
      }
    };

    setImages(updatedImages);
  };

  const handleAdditionalImageChange = async (file, index) => {
    const updatedImages = [...evidences];

    if (file) {
      const previewUrl = URL.createObjectURL(file);

      updatedImages[index] = {
        ...updatedImages[index],
        path: previewUrl,
        file
      };
    } else {
      updatedImages[index] = {
        ...updatedImages[index],
        path: null,
        file: null
      };
    }

    setEvidences(updatedImages);
  };

  const handleAdditionalDescriptionChange = (description, index) => {
    const updatedImages = [...evidences];

    updatedImages[index] = {
      ...updatedImages[index],
      description
    };

    setEvidences(updatedImages);
  };

  const handleAddEvidence = () => {
    if (!canAddMoreImages || addLockRef.current) return;
    if (startProtectedAction && !startProtectedAction("addEvidence")) return;
    addLockRef.current = true;
    setIsAdding(true);

    setEvidences([...evidences, createEmptyAdditionalImage()]);
  };

  const handleRemoveEmptyEvidence = (image) => {
    if (isDisabled || image.id || image.file || image.path) return;
    setEvidences((current) => current.filter((item) => item !== image));
  };

  useEffect(() => {
    if (!addLockRef.current) return;
    addLockRef.current = false;
    setIsAdding(false);
    endProtectedAction?.();
  }, [evidences.length, endProtectedAction]);

  return (
    <div>
      <br />

      <p className="createRist-title mt-25">
        3. Anexos y Evidencias Fotográficas
      </p>
      <Grid
        container
        justifyContent="center"
        spacing={2}
        style={{ pointerEvents: isViewer || isLocked ? "none" : "auto" }}
      >
        {Object.keys(images).map((key, index) => (
          <Grid item xs={12} md={6} key={key}>
            <div className="form-group">
              <p className="create-label m-0">
                Imagen del equipo biomédico {index + 1}
              </p>

              <ImageUploadButton
                loading={imagesLoading}
                onProcessingChange={onProcessingChange}
                isViewer={isApprover || isViewer || isLocked}
                path={images[key].path}
                onImageChange={(file) => handleImageChange(file, key)}
                buttonText={`Seleccionar Imagen`}
                disabled={isDisabled}
              />

              <input
                type="text"
                value={images[key].description || ""}
                onChange={(e) => handleDescriptionChange(e.target.value, key)}
                placeholder="Descripción de la imagen"
                className="custom-text-input"
                maxLength={200}
                disabled={isDisabled}
              />
            </div>
          </Grid>
        ))}

        {evidences.map((image, index) => {
          const imageNumber = MAIN_IMAGES_COUNT + index + 1;

          return (
            <Grid item xs={12} md={6} key={`additional-image-${index}`}>
              <div className="form-group">
                <div className="HContainer j-space-between a-center">
                  <p className="create-label m-0">
                    Evidencia adicional {imageNumber}
                  </p>
                  {!isApprover && !isViewer && !isLocked && !image.id && !image.file && !image.path && (
                    <Tooltip title="Quitar campo vacío">
                      <span>
                        <LaddaButton type="button" width={32} height={32} margin="0" borderRadius="50%"
                          backgroundColor="white" color="#d32f2f" borderColor="transparent"
                          loading={false}
                          aria-label="Quitar campo vacío" disabled={isDisabled}
                          onClick={() => handleRemoveEmptyEvidence(image)}>
                          <CloseIcon fontSize="small" />
                        </LaddaButton>
                      </span>
                    </Tooltip>
                  )}
                </div>

                <ImageUploadButton
                  loading={imagesLoading}
                  onProcessingChange={onProcessingChange}
                  isViewer={isApprover || isViewer || isLocked}
                  path={image.path}
                  onImageChange={(file) =>
                    handleAdditionalImageChange(file, index)
                  }
                  buttonText="Seleccionar Imagen"
                  disabled={isDisabled}
                />

                <input
                  type="text"
                  value={image.description || ""}
                  onChange={(e) =>
                    handleAdditionalDescriptionChange(e.target.value, index)
                  }
                  placeholder="Descripción de la imagen"
                  className="custom-text-input"
                  maxLength={200}
                  disabled={isDisabled}
                />
              </div>
            </Grid>
          );
        })}
      </Grid>

      {!(isApprover || isViewer || isLocked) && (
        <div className="HContainer j-end mt-20">
          <LaddaButton
            type="button"
            variant="contained"
            onClick={handleAddEvidence}
            disabled={!canAddMoreImages || isAdding}
            loading={isAdding}
            sx={{
              minWidth: "44px",
              height: "40px",
              borderRadius: "8px",
              textTransform: "none"
            }}
          >
            Agregar evidencia
          </LaddaButton>
        </div>
      )}

      <p className="text-form-label mt-10">
        Evidencias: {totalImages} / {MAX_TOTAL_IMAGES}
      </p>
    </div>
  );
};

export default AnnexesPhotographicEvidence;
